import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addDays,
} from "date-fns";
import type {
  CellStatus,
  DailyRegister,
  MonthlyCell,
  MonthlyRegister,
  MonthlyRow,
  RegisterRow,
  SessionKey,
} from "@/lib/types";
import { formatPresencePct, summarizeMonthlyRow } from "@/lib/attendance-pct";
import { getMonthlyDaySessions } from "@/lib/monthly";

export type ReportPeriod = "daily" | "weekly" | "monthly";

export interface ReportDayCell {
  status: CellStatus;
  label: string;
  late_minutes?: number | null;
}

export interface ReportPersonRow {
  person_id: string;
  person_name: string;
  category?: string;
  cells: Record<string, ReportDayCell | null>;
  presence_label: string;
  presence_pct: number | null;
  all_out: boolean;
  counts?: {
    on_leave: number;
    sick: number;
    taken_permission: number;
    mantra_submitted: number;
    fine_submitted: number;
  };
}

export interface AttendanceReport {
  period: ReportPeriod;
  title: string;
  subtitle: string;
  /** Dates newest-first for weekly (matches Excel), chronological for monthly */
  dates: string[];
  showPresence: boolean;
  presenceHeader: string;
  showStatusCounts?: boolean;
  rows: ReportPersonRow[];
}

function cellLabel(status: CellStatus, lateMinutes?: number | null): string {
  if (status === "pending") return "";
  if (status === "L" && lateMinutes != null) return `L${lateMinutes}`;
  return status;
}

function monthlyCellToReport(
  cell: MonthlyCell | undefined
): ReportDayCell | null {
  if (!cell) return null;
  const sessions = getMonthlyDaySessions(cell);
  if (sessions.length === 0) {
    if (cell.status === "pending") return null;
    return {
      status: cell.status,
      label: cellLabel(cell.status, cell.late_minutes),
      late_minutes: cell.late_minutes,
    };
  }
  if (sessions.length === 1) {
    const s = sessions[0];
    if (s.status === "pending") return null;
    return {
      status: s.status,
      label: cellLabel(s.status, s.late_minutes),
      late_minutes: s.late_minutes,
    };
  }
  // Multi-shift: show AM/PM compact e.g. P/A
  const labels = sessions.map((s) =>
    s.status === "pending" ? "—" : cellLabel(s.status, s.late_minutes)
  );
  const primary = sessions.find((s) => s.status !== "pending") ?? sessions[0];
  return {
    status: primary.status,
    label: labels.join("/"),
    late_minutes: primary.late_minutes,
  };
}

function rowFromMonthly(
  row: MonthlyRow,
  dates: string[]
): ReportPersonRow {
  const cells: Record<string, ReportDayCell | null> = {};

  for (const d of dates) {
    cells[d] = monthlyCellToReport(row.cells[d]);
  }

  const summary = summarizeMonthlyRow(row, dates);
  return {
    person_id: row.person_id,
    person_name: row.person_name,
    category: row.category,
    cells,
    presence_label: formatPresencePct(summary.present_pct, summary.all_out),
    presence_pct: summary.present_pct,
    all_out: summary.all_out,
    counts: {
      on_leave: summary.on_leave,
      sick: summary.sick,
      taken_permission: summary.taken_permission,
      mantra_submitted: summary.mantra_submitted ?? 0,
      fine_submitted: summary.fine_submitted ?? 0,
    },
  };
}

function primarySessionKey(
  row: RegisterRow,
  preferred: SessionKey[]
): SessionKey | null {
  for (const key of preferred) {
    if (row.columns[key]) return key;
  }
  const keys = Object.keys(row.columns) as SessionKey[];
  return keys[0] ?? null;
}

/** Daily report: one status column for the selected date. */
export function buildDailyReport(
  register: DailyRegister,
  options?: { groupCategory?: string; title?: string }
): AttendanceReport {
  const preferred: SessionKey[] = [
    "morning",
    "morning_block",
    "evening_block",
    "employee_shift",
  ];

  const rows: ReportPersonRow[] = [];
  for (const group of register.groups) {
    if (
      options?.groupCategory &&
      group.category !== options.groupCategory
    ) {
      continue;
    }
    for (const row of group.rows) {
      const key = primarySessionKey(row, preferred);
      const cell = key ? row.columns[key] ?? null : null;
      const status = (cell?.status ?? "pending") as CellStatus;
      const reportCell: ReportDayCell | null =
        !cell || status === "pending"
          ? null
          : {
              status,
              label: cellLabel(status, cell.late_minutes),
              late_minutes: cell.late_minutes,
            };

      rows.push({
        person_id: row.person_id,
        person_name: row.person_name,
        category: group.category,
        cells: { [register.date]: reportCell },
        presence_label: "—",
        presence_pct: null,
        all_out: false,
      });
    }
  }

  const dateLabel = format(parseISO(register.date), "dd/MM/yyyy");
  return {
    period: "daily",
    title: options?.title ?? "Daily Presence",
    subtitle: dateLabel,
    dates: [register.date],
    showPresence: false,
    presenceHeader: "",
    rows,
  };
}

/** Build weekly/monthly report from monthly API rows. */
export function buildPeriodReport(
  monthly: MonthlyRegister,
  dates: string[],
  options: {
    period: "weekly" | "monthly";
    tableKey?: string;
    title: string;
    presenceHeader: string;
  }
): AttendanceReport {
  const table =
    monthly.tables.find((t) => t.key === (options.tableKey ?? "yoga")) ??
    monthly.tables[0];

  // Always oldest → newest left to right
  const sortedDates = [...dates].sort((a, b) => a.localeCompare(b));

  const rows = (table?.rows ?? []).map((row) => rowFromMonthly(row, sortedDates));

  const first = sortedDates[0] ?? dates[0];
  const last = sortedDates[sortedDates.length - 1] ?? dates[dates.length - 1];
  const subtitle =
    options.period === "weekly" && first && last
      ? `${format(parseISO(first), "dd/MM/yyyy")} – ${format(parseISO(last), "dd/MM/yyyy")}`
      : monthly.month;

  return {
    period: options.period,
    title: options.title,
    subtitle,
    dates: sortedDates,
    showPresence: true,
    presenceHeader: options.presenceHeader,
    showStatusCounts: options.period === "monthly",
    rows,
  };
}

/** Monday–Sunday week containing `dateStr` (yyyy-MM-dd). */
export function weekDatesContaining(dateStr: string): string[] {
  const d = parseISO(dateStr);
  const start = startOfWeek(d, { weekStartsOn: 1 });
  const end = endOfWeek(d, { weekStartsOn: 1 });
  return eachDayOfInterval({ start, end }).map((day) =>
    format(day, "yyyy-MM-dd")
  );
}

export function weekLabel(dateStr: string): string {
  const dates = weekDatesContaining(dateStr);
  if (dates.length === 0) return dateStr;
  return `${format(parseISO(dates[0]), "dd MMM")} – ${format(parseISO(dates[dates.length - 1]), "dd MMM yyyy")}`;
}

/** Months needed to cover a set of dates (yyyy-MM). */
export function monthsForDates(dates: string[]): string[] {
  const set = new Set(dates.map((d) => d.slice(0, 7)));
  return [...set].sort();
}

export function shiftWeek(dateStr: string, deltaWeeks: number): string {
  return format(addDays(parseISO(dateStr), deltaWeeks * 7), "yyyy-MM-dd");
}

export function formatReportDateHeader(dateStr: string): string {
  return format(parseISO(dateStr), "dd/MM/yyyy");
}
